import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Scale, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { QnASection } from '../components/shared/QnASection';
import { RichTextEditor } from '../components/RichTextEditor';
import Flag from '../components/world/Flag';
import { useAuth } from '../context/AuthContext';
import { useJazyk } from '../context/LanguageContext';
import { API } from '../lib/api';

/**
 * Právní poradna pro jednu zemi.
 *
 * Česká poradna má články a otázky. Tady je totéž, jen vázané na zemi:
 * článek napsaný k USA se v Japonsku neukáže a naopak. Otázky i články bez
 * země zůstávají tam, kde byly — v české poradně.
 *
 * Nahoře je upozornění, které má i profil země. Odpovídají tu ověření
 * právníci a jejich odznak nerozlišuje, ke které zemi se vztahuje — proto se
 * tu říká nahlas, že tohle není právní rada a je potřeba si to ověřit u zdroje
 * nebo u organizace v té zemi.
 */
export default function CountryLegalPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, datum, dvojjazycne } = useJazyk();
  const { user, isAdmin } = useAuth();

  const [zeme, setZeme] = useState(null);
  const [clanky, setClanky] = useState([]);
  const [kategorie, setKategorie] = useState([]);
  const [nacita, setNacita] = useState(true);
  const [chyba, setChyba] = useState('');
  const [otevren, setOtevren] = useState(false);

  const jePravnik = isAdmin || user?.role === 'lawyer';

  useEffect(() => {
    axios.get(`${API}/article-categories`)
      .then((r) => setKategorie(r.data || []))
      .catch(() => { /* bez kategorií se jen nedá vybírat */ });
  }, []);

  const nacti = useCallback(async () => {
    setNacita(true);
    setChyba('');
    try {
      const z = await axios.get(`${API}/geo/countries/${slug}`);
      setZeme(z.data);
      // Právnička a správkyně vidí i nepublikované, jako v české poradně.
      const cesta = jePravnik ? `${API}/admin/articles` : `${API}/articles`;
      const { data } = await axios.get(cesta, { params: { country: z.data.iso2 } });
      setClanky(data || []);
    } catch (e) {
      setChyba(e?.response?.status === 404 ? 'neznamaZeme' : 'chyba');
    } finally {
      setNacita(false);
    }
  }, [slug, jePravnik]);

  useEffect(() => { nacti(); }, [nacti]);

  if (chyba) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-bloom-sub">
          {chyba === 'neznamaZeme' ? t('zeme.neznama') : t('vypis.chyba')}
        </p>
        <button type="button" onClick={() => navigate('/world')}
                className="mt-4 inline-flex items-center gap-1.5 text-bloom-violet font-medium">
          <ArrowLeft className="w-4 h-4" /> {t('zeme.mapaSveta')}
        </button>
      </div>
    );
  }

  const nazevZeme = dvojjazycne(zeme?.name_cs, zeme?.name_en);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="country-legal">
      <button type="button" onClick={() => navigate(`/world/${slug}`)}
              className="inline-flex items-center gap-1.5 text-sm text-bloom-sub hover:text-bloom-violet">
        <ArrowLeft className="w-4 h-4" /> {t('detail.zpetNa', { zeme: nazevZeme })}
      </button>

      <header className="mt-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {zeme && <Flag emoji={zeme.flag} iso2={zeme.iso2} size="lg" />}
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-bloom-text">
              {t('sekce.legal')}
            </h1>
            <p className="text-bloom-sub text-sm">{nazevZeme}</p>
          </div>
        </div>

        {zeme && jePravnik && (
          <NovyClanek zeme={zeme} kategorie={kategorie} otevren={otevren}
                      nastavOtevren={setOtevren} poUlozeni={nacti} />
        )}
      </header>

      {/* Odznak „ověřený právník" neříká, ke které zemi se váže. */}
      <p className="mt-5 flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50/60
                    p-3 text-xs text-bloom-sub leading-relaxed">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
        {t('profil.upozorneni')}
      </p>

      <section className="mt-6">
        <h2 className="font-heading text-lg font-bold text-bloom-text">
          {t('prav.clankyAPruvodci')}
        </h2>

        {nacita ? (
          <div className="py-12 flex justify-center"><div className="spinner" /></div>
        ) : clanky.length === 0 ? (
          <p className="py-10 text-center text-bloom-sub">{t('prav.zadneClanky')}</p>
        ) : (
          <ul className="mt-4 space-y-3" data-testid="zeme-clanky">
            {clanky.map((c) => (
              <li key={c.id} className="bg-white border border-border/40 rounded-2xl p-4">
                <h3 className="font-heading font-bold text-bloom-text">{c.title}</h3>
                <p className="mt-1 text-xs text-bloom-sub">
                  {c.author_name}{c.created_at ? ` · ${datum(c.created_at)}` : ''}
                </p>
                {/* Obsah píšou lidé a je z editoru, server ho čistí při uložení. */}
                <div className="mt-2 text-sm text-bloom-sub leading-relaxed prose-bloom"
                     dangerouslySetInnerHTML={{ __html: c.content }} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {zeme && (
        <div className="mt-12">
          <QnASection
            section="legal"
            country={zeme.iso2}
            canAnswer={jePravnik}
            canDelete={jePravnik}
            showVoteCount
            accentColor="violet"
            legalContext
            testIdPrefix="zeme-pravo-otazka"
            addButtonLabel={t('prav.pridatOtazku')}
            dialogTitle={t('prav.novaOtazka')}
            dialogPlaceholder={t('prav.naCoSeZeptat')}
            user={user}
            categories={kategorie.map((k) => ({ id: k.id, name: k.name }))}
            activeCategory="all"
          />
        </div>
      )}
    </div>
  );
}

/** Nový článek do poradny téhle země. Píšou jen ověření právníci a správkyně. */
function NovyClanek({ zeme, kategorie, otevren, nastavOtevren, poUlozeni }) {
  const { t } = useJazyk();
  const [nazev, setNazev] = useState('');
  const [obsah, setObsah] = useState('');
  const [kat, setKat] = useState('');
  const [odesila, setOdesila] = useState(false);

  useEffect(() => {
    if (kategorie.length && !kat) setKat(kategorie[0].id);
  }, [kategorie, kat]);

  const uloz = async (e) => {
    e.preventDefault();
    if (odesila) return;
    setOdesila(true);
    try {
      await axios.post(`${API}/articles`, {
        title: nazev, content: obsah, category: kat,
        published: true, country_iso2: zeme.iso2,
      });
      toast.success(t('prav.clanekPridan'));
      nastavOtevren(false);
      setNazev(''); setObsah('');
      poUlozeni();
    } catch {
      toast.error(t('prav.chybaPridat'));
    } finally {
      setOdesila(false);
    }
  };

  return (
    <Dialog open={otevren} onOpenChange={nastavOtevren}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-bloom-violet text-white shrink-0" data-testid="zeme-novy-clanek">
          <Plus className="w-4 h-4 mr-1" />{t('prav.pridatClanek')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-bloom-text flex items-center gap-2">
            <Scale className="w-4 h-4 text-bloom-violet" />{t('prav.novyClanek')}
          </DialogTitle>
          <DialogDescription>{t('prav.vytvorteClanek')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={uloz} className="space-y-3">
          <div>
            <Label className="text-bloom-text text-sm">{t('prav.nazev')}</Label>
            <Input value={nazev} onChange={(e) => setNazev(e.target.value)} required
                   data-testid="zeme-clanek-nazev" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-bloom-text text-sm">{t('prav.kategorie')}</Label>
              <Select value={kat} onValueChange={setKat}>
                <SelectTrigger><SelectValue placeholder={t('odb.vyber')} /></SelectTrigger>
                <SelectContent>
                  {kategorie.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-bloom-text text-sm">{t('odb.stat')}</Label>
              {/* Stát se nevybírá – je daný stránkou, na které formulář stojí. */}
              <div className="h-9 flex items-center gap-2 px-3 rounded-md border border-input
                              bg-muted/40 text-sm">
                <Flag emoji={zeme.flag} iso2={zeme.iso2} size="sm" />
                {zeme.name_cs || zeme.name_en}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-bloom-text text-sm">{t('prav.obsah')}</Label>
            <RichTextEditor value={obsah} onChange={setObsah}
                            placeholder={t('prav.obsahPriklad')} />
          </div>

          <Button type="submit" className="w-full bg-bloom-violet text-white"
                  disabled={odesila || !obsah.trim()}>
            {odesila ? t('akt.ukladam') : t('prav.pridatClanek')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
